import { Box, Checkbox, Chip, Skeleton, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { Mail, Factory, Users } from "lucide-react"

import type { ContactList } from "@crm-src/lib/contact-lists-api"

import type { EmailRecipient } from "../email-target-selector"
import { ConfidenceChip, TYPE_CHIP_COLOR, typeRowSx } from "./chips"
import { memberCount } from "@crm-src/pages/contact-groups/membership"

/**
 * The three "browse" tab bodies of the email target selector — the unified
 * search-results list, the live-by-industry segment picker, and the contact-
 * group picker — extracted from `email-target-selector.tsx`. No money / reseller
 * here — wall-neutral.
 */

interface UnifiedContactListProps {
  contacts: EmailRecipient[]
  onContactToggle: (contact: EmailRecipient, checked: boolean) => void
  recipients: EmailRecipient[]
  isLoading: boolean
}

function getTypeBadge(type: string) {
  const color = TYPE_CHIP_COLOR[type] || "default"
  const label = type === 'customer' ? 'Customer'
    : type === 'prospect' ? 'Prospect'
    : type === 'ai_prospect' ? 'AI Prospect'
    : type === 'contact' ? 'Contact'
    : type === 'manual' ? 'Manual'
    : 'Other'
  return <Chip size="small" color={color} label={label} sx={{ fontSize: 12 }} />
}

/** Unified search-results list — recipients of any type with a per-row type badge. */
export function UnifiedContactList({ contacts, onContactToggle, recipients, isLoading }: UnifiedContactListProps) {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Skeleton variant="rounded" sx={{ width: 16, height: 16 }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="rounded" sx={{ width: 128, height: 16, mb: 0.5 }} />
              <Skeleton variant="rounded" sx={{ width: 192, height: 12 }} />
            </Box>
          </Box>
        ))}
      </Box>
    )
  }

  return (
    <Box sx={{ height: 320, overflow: "auto" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {contacts.map((contact) => {
          const isSelected = recipients.some(r => r.id === contact.id)

          return (
            <Box
              key={contact.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                py: 0.75,
                px: 1.25,
                border: 1,
                borderRadius: 1,
                transition: "background-color .15s",
                cursor: "pointer",
                "&:hover": { bgcolor: "action.hover" },
                ...(isSelected ? typeRowSx(contact.type) : { bgcolor: "background.paper", borderColor: "divider" }),
              }}
              onClick={() => onContactToggle(contact, !isSelected)}
            >
              <Checkbox
                checked={isSelected}
                onChange={() => {}} // Handled by parent onClick
                size="small"
                sx={{ flexShrink: 0, p: 0 }}
              />

              {/* Contact Avatar */}
              <Box sx={{ flexShrink: 0, width: 26, height: 26, bgcolor: "surfaceSoft", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography component="span" sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary" }}>
                  {contact.name ? contact.name.charAt(0).toUpperCase() : contact.email ? contact.email.charAt(0).toUpperCase() : '?'}
                </Typography>
              </Box>

              {/* Contact Info */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography component="span" sx={{ fontWeight: 500, fontSize: 13, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {contact.name || contact.email || 'Unknown Contact'}
                  </Typography>
                  {getTypeBadge(contact.type)}
                  {contact.type === 'ai_prospect' && <ConfidenceChip confidence={contact.email_confidence} />}
                </Box>
                <Box sx={{ fontSize: 11.5, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center" }}>
                  <Box component="span" sx={{ mr: 0.5, display: "inline-flex" }}><Mail size={11} /></Box>
                  {contact.email}
                  {contact.company && <Box component="span" sx={{ mx: 0.5, color: "text.disabled" }}>·</Box>}
                  {contact.company && <Box component="span" sx={{ color: "text.disabled", overflow: "hidden", textOverflow: "ellipsis" }}>{contact.company}</Box>}
                </Box>
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

// IndustrySegmentList — pick one or more industries to email EVERY directory
// contact in them. Unlike picking individuals, this is a live segment: the
// campaign re-resolves the matching contacts at send/recount time.
interface IndustrySegmentListProps {
  selected: string[]
  counts: Record<string, number> | null
  loading: boolean
  onToggle: (industry: string, checked: boolean) => void
}

export function IndustrySegmentList({ selected, counts, loading, onToggle }: IndustrySegmentListProps) {
  // The REAL industries that have emailable contacts (from the server), busiest
  // first; a currently-selected industry always stays visible.
  const industries = Array.from(new Set([...Object.keys(counts ?? {}), ...selected]))
    .sort((a, b) => (counts?.[b] ?? 0) - (counts?.[a] ?? 0) || a.localeCompare(b))

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ fontSize: 12, color: "text.secondary", pb: 1, borderBottom: 1, borderColor: "divider" }}>
        Email everyone in an industry — picks every contact tied to a customer or prospect tagged
        with that industry. The campaign re-resolves the matching contacts at send time.
      </Box>
      <Box sx={{ height: 256, overflow: "auto" }}>
        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {[...Array(4)].map((_, i) => <Skeleton key={i} variant="rounded" sx={{ height: 44 }} />)}
          </Box>
        ) : industries.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
            <Box sx={{ mx: "auto", width: 56, height: 56, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5, bgcolor: "surfaceSoft" }}>
              <Box sx={{ color: "text.disabled", display: "flex" }}><Factory size={22} /></Box>
            </Box>
            <Typography sx={{ fontSize: 14 }}>No industries yet</Typography>
            <Typography sx={{ fontSize: 12, mt: 0.5 }}>
              Tag your customers &amp; prospects with an industry — their contacts then show up here.
            </Typography>
          </Box>
        ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {industries.map((label) => {
            const isSelected = selected.includes(label)
            const count = counts?.[label]
            return (
              <Box
                key={label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  py: 0.75,
                  px: 1.25,
                  border: 1,
                  borderRadius: 1,
                  transition: "background-color .15s",
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                  ...(isSelected
                    ? { bgcolor: (t) => alpha(t.palette.info.main, 0.08), borderColor: (t) => alpha(t.palette.info.main, 0.3) }
                    : { bgcolor: "background.paper", borderColor: "divider" }),
                }}
                onClick={() => onToggle(label, !isSelected)}
              >
                <Checkbox checked={isSelected} onChange={() => {}} size="small" sx={{ flexShrink: 0, p: 0 }} />
                <Box sx={{ flexShrink: 0, width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: (t) => alpha(t.palette.info.main, 0.18) }}>
                  <Box sx={{ color: "info.main", display: "flex" }}><Factory size={14} /></Box>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography component="span" sx={{ fontWeight: 500, fontSize: 13, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {label}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  {loading ? (
                    <Skeleton variant="rounded" sx={{ width: 36, height: 18, flexShrink: 0 }} />
                  ) : count !== undefined ? (
                    <Chip size="small" color={count > 0 ? "info" : "default"} label={`${count} contact${count !== 1 ? 's' : ''}`} sx={{ fontSize: 11, height: 18, flexShrink: 0, "& .MuiChip-label": { px: 0.75 } }} />
                  ) : null}
                </Box>
              </Box>
            )
          })}
        </Box>
        )}
      </Box>
    </Box>
  )
}

// GroupList component for selecting contact groups
interface GroupListProps {
  groups: ContactList[]
  selectedGroups: Set<number>
  onGroupToggle: (groupId: number, checked: boolean) => void
  isLoading: boolean
}

export function GroupList({ groups, selectedGroups, onGroupToggle, isLoading }: GroupListProps) {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Skeleton variant="rounded" sx={{ width: 16, height: 16 }} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="rounded" sx={{ width: 128, height: 16, mb: 0.5 }} />
              <Skeleton variant="rounded" sx={{ width: 192, height: 12 }} />
            </Box>
          </Box>
        ))}
      </Box>
    )
  }

  if (groups.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
        <Box sx={{ mx: "auto", width: 64, height: 64, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5, border: 1, borderColor: (t) => alpha(t.palette.warning.main, 0.3), bgcolor: (t) => alpha(t.palette.warning.main, 0.08) }}>
          <Box sx={{ color: "warning.main", display: "flex" }}><Users size={24} /></Box>
        </Box>
        <Typography sx={{ fontSize: 14 }}>No contact groups found</Typography>
        <Typography sx={{ fontSize: 12, mt: 0.5 }}>Create groups from the Contact Groups page</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ fontSize: 12, color: "text.secondary", pb: 1, borderBottom: 1, borderColor: "divider" }}>
        Select groups to send to all their members at once
      </Box>
      <Box sx={{ height: 256, overflow: "auto" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {groups.map((group) => {
            const isSelected = selectedGroups.has(group.id)
            const count = memberCount(group)

            return (
              <Box
                key={group.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  py: 0.75,
                  px: 1.25,
                  border: 1,
                  borderRadius: 1,
                  transition: "background-color .15s",
                  cursor: "pointer",
                  "&:hover": { bgcolor: "action.hover" },
                  ...(isSelected
                    ? { bgcolor: (t) => alpha(t.palette.warning.main, 0.08), borderColor: (t) => alpha(t.palette.warning.main, 0.3) }
                    : { bgcolor: "background.paper", borderColor: "divider" }),
                }}
                onClick={() => onGroupToggle(group.id, !isSelected)}
              >
                <Checkbox
                  checked={isSelected}
                  onChange={() => {}}
                  size="small"
                  sx={{ flexShrink: 0, p: 0 }}
                />

                {/* Group Icon */}
                <Box sx={{ flexShrink: 0, width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: (t) => alpha(t.palette.warning.main, 0.18) }}>
                  <Box sx={{ color: "warning.main", display: "flex" }}><Users size={14} /></Box>
                </Box>

                {/* Group Info */}
                <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography component="span" sx={{ fontWeight: 500, fontSize: 13, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {group.name}
                  </Typography>
                  {group.description && (
                    <Typography component="span" sx={{ fontSize: 11.5, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      — {group.description}
                    </Typography>
                  )}
                  <Box sx={{ flex: 1 }} />
                  <Chip size="small" color="warning" label={`${count} member${count !== 1 ? 's' : ''}`} sx={{ fontSize: 11, height: 18, flexShrink: 0, "& .MuiChip-label": { px: 0.75 } }} />
                </Box>
              </Box>
            )
          })}
        </Box>
      </Box>
    </Box>
  )
}
